import { useEffect, useState } from 'react'
import cable from '@/frontend/lib/cable'
import type { Publication } from '@/frontend/features/DashboardRuntime/types'

// Cable invalidates; the authorized HTTP snapshot is the source of truth.
// Reconnect and tab focus recover missed events without continuous polling.
export function usePublication(initial: Publication, path: string) {
  const [publication, setPublication] = useState(initial)
  const [denied, setDenied] = useState(false)
  useEffect(() => {
    setPublication(initial)
    setDenied(false)
    let disposed = false
    let generation = 0
    let pending: AbortController | null = null
    const reload = async () => {
      const current = ++generation
      pending?.abort()
      pending = new AbortController()
      try {
        const response = await fetch(`${path}/status`, { signal: pending.signal, headers: { Accept: 'application/json' }, redirect: 'error' })
        if (disposed || current !== generation) return
        if ([401, 403, 404].includes(response.status)) { setDenied(true); return }
        if (!response.ok) return
        const next = await response.json() as Publication
        if (!disposed && current === generation && next.id === initial.id) { setPublication(next); setDenied(false) }
      } catch { /* Keep the visible revision during transient reconnects. */ }
    }
    const subscription = cable.subscriptions.create({ channel: 'DashboardPublicationChannel', publication_id: initial.id }, {
      connected: reload, received: reload, rejected: reload,
    })
    window.addEventListener('focus', reload)
    return () => { disposed = true; pending?.abort(); subscription.unsubscribe(); window.removeEventListener('focus', reload) }
  }, [initial, path])
  // Inertia can reuse this page component for another publication. Never show
  // the previous project's state during the effect/reset render boundary.
  return { publication: publication.id === initial.id ? publication : initial,
    denied: publication.id === initial.id && denied }
}
